class LazyVideo extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: contents;

        opacity: 0;

        transition: opacity 0.5s ease;
      }
      :host([loaded]) {
        opacity: 1;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    this.audioGainNode = null

    this.video = window.BAO.Utils.getElement('video', { context: this })

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })
    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video)

    this.setupPosterForViewport()
    this.setupMediaListeners()
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    // Skip hidden videos (defence in depth)
    const style = window.getComputedStyle(this.video)
    if (style.display === 'none' || style.visibility === 'hidden') return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion) {
      this.video.removeAttribute('autoplay')
      this.video.setAttribute('controls', '')
      this.video.pause()
    }

    // Posters are deferred via data-poster so offscreen videos don't download them on page load
    if (this.video.dataset.poster && !this.video.hasAttribute('poster')) {
      this.video.setAttribute('poster', this.video.dataset.poster)
    }

    this.activateSources()
    observer.unobserve(this.video)

    if (this.video.hasAttribute('crossorigin')) {
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256

        this.audioGainNode = audioCtx.createGain()
        this.audioGainNode.gain.setValueAtTime(0, audioCtx.currentTime)

        const source = audioCtx.createMediaElementSource(this.video)
        source.connect(analyser)
        analyser.connect(this.audioGainNode)
        this.audioGainNode.connect(audioCtx.destination)

        this.video.addEventListener('playing', () => {
          const wasMuted = this.video.muted
          const originalVolume = this.video.volume

          this.video.muted = false
          this.video.volume = 1.0

          const dataArray = new Uint8Array(analyser.frequencyBinCount)
          let sampleCount = 0
          let soundDetected = false

          const verifyAudio = () => {
            analyser.getByteFrequencyData(dataArray)
            const totalVolume = dataArray.reduce((sum, val) => sum + val, 0)

            if ((totalVolume / dataArray.length) > 1) {
              soundDetected = true
            }

            sampleCount++

            if (sampleCount < 15 && !soundDetected) {
              requestAnimationFrame(verifyAudio)
            } else {
              this.video.muted = wasMuted
              this.video.volume = originalVolume

              if (soundDetected) {
                this.removeAttribute('is-silent')
              } else {
                this.setAttribute('is-silent', '')
              }
            }
          }

          verifyAudio()
        }, { once: true })
      } catch (e) {
        console.error(e)
      }
    }

    this.setAttribute('loaded', '')

    if (!prefersReducedMotion && this.video.hasAttribute('autoplay')) {
      this.video.play().catch(() => {})
    }

    if (
      window.innerWidth > 768 ||
      this.video.hasAttribute('autoplay') === false
    ) {
      return
    }

    if (this.video.paused) {
      document.addEventListener('touchstart', () => {
        if (this.video.paused && !this.video.hasAttribute('initialised')) {
          this.video.play()
          this.video.setAttribute('initialised', true)
        }
      })
    }
  }

  setMuteState (isMuted) {
    this.video.muted = isMuted

    if (this.audioGainNode) {
      this.audioGainNode.gain.value = isMuted ? 0 : 1
    }
  }

  activateSources () {
    const sources = this.video.querySelectorAll('source')
    let activated = false

    sources.forEach((source) => {
      const media = source.getAttribute('media')
      const dataSrc = source.dataset.src
      const type = source.getAttribute('type') || ''

      if (
        type.toLowerCase() === 'application/x-mpegurl' ||
        (dataSrc && dataSrc.toLowerCase().endsWith('.m3u8'))
      ) {
        return
      }

      if (
        dataSrc &&
        (!media || window.matchMedia(media).matches)
      ) {
        source.src = dataSrc
        activated = true
      } else if (source.src && media && !window.matchMedia(media).matches) {
        source.removeAttribute('src')
      }
    })

    if (activated) {
      this.video.setAttribute('preload', 'auto')
      this.video.load()
    }
  }

  setupPosterForViewport () {
    const posterDesktop = this.video.dataset.posterDesktop
    const posterMobile = this.video.dataset.posterMobile
    const posterDesktopMedia = this.video.dataset.posterDesktopMedia

    if (!posterDesktop || !posterMobile || !posterDesktopMedia) return

    const mqDesktop = window.matchMedia(posterDesktopMedia)

    const updatePoster = () => {
      if (mqDesktop.matches) {
        this.video.setAttribute('poster', posterDesktop)
      } else {
        this.video.setAttribute('poster', posterMobile)
      }
    }

    updatePoster()
    mqDesktop.addEventListener('change', updatePoster)
  }

  setupMediaListeners () {
    const sources = Array.from(this.video.querySelectorAll('source'))
    if (!sources.length) return

    const mediaQueries = new Set()

    sources.forEach((source) => {
      const media = source.getAttribute('media')
      if (media) {
        mediaQueries.add(media)
      }
    })

    mediaQueries.forEach((query) => {
      const mq = window.matchMedia(query)
      const handler = () => {
        this.updateActiveSources()
      }
      mq.addEventListener('change', handler)
    })
  }

  updateActiveSources () {
    const wasPlaying = !this.video.paused

    this.activateSources()

    if (wasPlaying && this.video.hasAttribute('autoplay')) {
      this.video.play().catch(() => {})
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}

class LazyVideoControls extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.handlePlayClick = this.handlePlayClick.bind(this)
    this.handleSoundClick = this.handleSoundClick.bind(this)

    if (this.els?.play?.exists === true) {
      this.listeners.add(this.els.play.element, 'click', this.handlePlayClick)
    }

    if (this.els?.sound?.exists === true) {
      this.listeners.add(this.els.sound.element, 'click', this.handleSoundClick)
    }
  }

  muteVideo () {
    const section = this.closest(this?.externalEls?.section?.selector)

    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded'),
    )

    videos.forEach((el) => {
      const video = el.video

      if (video.muted === false) {
        video.muted = true
        this.updateSoundButton(true, section)
      }
    })
  }

  handleSoundClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)

    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded'),
    )

    videos.forEach((el) => {
      const video = el.video

      if (video.hasAttribute('data-play-one-lazy-video')) {
        const allVideos = document.querySelectorAll('[data-play-one-lazy-video]')

        allVideos.forEach((otherVideo) => {
          const otherLazyVideo = otherVideo.closest('lazy-video')
          if (otherLazyVideo) {
            otherLazyVideo.setMuteState(true)
          } else {
            otherVideo.muted = true
          }
          this.updateSoundButton(true, otherVideo.closest(this?.externalEls?.section?.selector))
        })
      }

      if (video.muted === true) {
        el.setMuteState(false)
        this.updateSoundButton(false, section)
      } else {
        el.setMuteState(true)
        this.updateSoundButton(true, section)
      }
    })
  }

  updateSoundButton (isMuted, section) {
    const buttons = section.querySelectorAll(this.els.sound.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isMuted ? 'false' : 'true')

      const playText = this.querySelector(this.els['sound-text'].selector)
      playText.textContent = isMuted ? this.dataset.unmute : this.dataset.mute
    })
  }

  handlePlayClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)

    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded'),
    )

    section.setAttribute(
      'data-paused',
      videos.some((el) => !el.video.paused),
    )

    videos.forEach((el) => {
      const video = el.video
      const videoWasPaused = video.paused

      if (video.hasAttribute('data-play-one-lazy-video')) {
        const allVideos = document.querySelectorAll('[data-play-one-lazy-video]')

        allVideos.forEach((otherVideo) => {
          otherVideo.pause()
          this.updatePlayPauseButton(false, otherVideo.closest(this?.externalEls?.section?.selector))
        })
      }

      if (videoWasPaused) {
        video.play()
        this.updatePlayPauseButton(true, section)
      } else {
        if (video.paused === false) {
          video.pause()
          this.updatePlayPauseButton(false, section)
        }
      }
    })
  }

  updatePosterImage (isPlaying, section) {
    const posterImages = section.querySelectorAll(this.externalEls.poster.selector)

    posterImages.forEach((posterImage) => {
      posterImage.setAttribute('aria-hidden', isPlaying ? 'true' : 'false')
    })
  }

  updatePlayPauseButton (isPlaying, section) {
    const buttons = section.querySelectorAll(this.els.play.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isPlaying ? 'false' : 'true')

      const playText = this.querySelector(this.els['play-text'].selector)
      playText.textContent = isPlaying ? this.dataset.pause : this.dataset.play
    })
  }
}

if (!customElements.get('lazy-video-controls')) {
  customElements.define('lazy-video-controls', LazyVideoControls)
}
